import { type SupplierListItem } from '@choco-admin/types';
import { Button, Card } from '@heroui/react';
import React, { useState } from 'react';

import { AlertCard } from '../../../../../../components/AlertCard';

import {
  CustomerTaxProfileTable,
  TaxJurisdictionDetailModal,
  TaxJurisdictionTable,
} from './components';
import { useChatTaxProfileList } from './hooks/useChatTaxProfileList';
import { useTaxJurisdictionList } from './hooks/useTaxJurisdictionList';
import { type TaxJurisdictionRow } from './types';

interface TaxTabProps {
  supplier?: SupplierListItem;
}

export const TaxTab: React.FC<TaxTabProps> = ({ supplier }) => {
  const [selectedJurisdiction, setSelectedJurisdiction] =
    useState<TaxJurisdictionRow | null>(null);

  const {
    jurisdictions,
    isLoading,
    isLoadingMore,
    errorMessage,
    loadMoreErrorMessage,
    hasNextPage,
    loadMore,
  } = useTaxJurisdictionList({ vendorId: supplier?.id });

  const customerList = useChatTaxProfileList({ vendorId: supplier?.id });

  if (!supplier) {
    return (
      <AlertCard
        type="info"
        title="Create distributor first"
        message="Please save the distributor to view tax settings."
      />
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-8 rounded-xl">
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold mb-1">Tax jurisdictions</h3>
            <p className="text-sm text-default-500">
              Read-only view of this distributor&apos;s jurisdictions. Open a
              row to see its rates.
            </p>
          </div>

          {errorMessage ? (
            <AlertCard
              type="error"
              title="Could not load tax jurisdictions"
              message={errorMessage}
            />
          ) : (
            <>
              <TaxJurisdictionTable
                jurisdictions={jurisdictions}
                isLoading={isLoading}
                onSelectJurisdiction={setSelectedJurisdiction}
              />

              {loadMoreErrorMessage && (
                <AlertCard
                  type="error"
                  title="Could not load more jurisdictions"
                  message={loadMoreErrorMessage}
                />
              )}

              {hasNextPage && (
                <div className="flex justify-center">
                  <Button
                    variant="flat"
                    color="default"
                    onPress={loadMore}
                    isLoading={isLoadingMore}
                  >
                    Load more
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </Card>

      <Card className="p-8 rounded-xl">
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold mb-1">Customers</h3>
            <p className="text-sm text-default-500">
              Every customer of this distributor with a tax jurisdiction
              assigned.
            </p>
          </div>

          {customerList.errorMessage ? (
            <AlertCard
              type="error"
              title="Could not load customers"
              message={customerList.errorMessage}
            />
          ) : (
            <>
              <CustomerTaxProfileTable
                customers={customerList.customers}
                isLoading={customerList.isLoading}
              />

              {customerList.loadMoreErrorMessage && (
                <AlertCard
                  type="error"
                  title="Could not load more customers"
                  message={customerList.loadMoreErrorMessage}
                />
              )}

              {customerList.hasNextPage && (
                <div className="flex justify-center">
                  <Button
                    variant="flat"
                    color="default"
                    onPress={customerList.loadMore}
                    isLoading={customerList.isLoadingMore}
                  >
                    Load more
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </Card>

      <TaxJurisdictionDetailModal
        jurisdiction={selectedJurisdiction}
        onClose={() => setSelectedJurisdiction(null)}
      />
    </div>
  );
};
